import React from 'react';
import { BlogCardPaginationButton } from '@/components/blogHome/BlogCardPaginationButton';
import { ChevronLeftIcon } from '@/assets/ChevronLeftIcon';
import { ChevronRightIcon } from '@/assets/ChevronRightIcon';

const buildPaginatedBlogUrl = (
  page: number,
  selectedCategory?: string,
  path?: string,
): string => {
  const params = new URLSearchParams();

  if (selectedCategory) {
    params.append('category', selectedCategory);
  }
  params.append('page', page.toString());

  return path ? `${path}?${params.toString()}` : `/blog?${params.toString()}`;
};

const buildPagination = (
  current: number,
  total: number,
  delta: number = 2,
): number[] => {
  const min = Math.max(1, current - delta);
  const max = Math.min(total, current + delta);
  const length = max - min + 1;

  return new Array(length).fill(null).map((_, i) => min + i);
};

interface BlogCardPaginationProps {
  children?: React.ReactNode;
  selectedCategory?: string;
  hasPrevPage: boolean;
  hasNextPage: boolean;
  currentPage: number;
  totalPages: number;
  path?: string;
}

export const BlogCardPagination = ({
  children,
  selectedCategory,
  hasPrevPage,
  hasNextPage,
  currentPage,
  totalPages,
  path,
}: BlogCardPaginationProps) => {
  if (totalPages < 1) {
    return null;
  }

  const pagination = buildPagination(currentPage, totalPages);
  const shouldRenderLeftLimit = pagination[0] > 1;
  const shouldRenderRightLimit =
    pagination[pagination.length - 1] !== totalPages;

  return (
    <div className="flex flex-col items-center gap-8">
      <div className="flex flex-row flex-wrap w-full justify-around gap-y-8 md:gap-x-16">
        {children}
      </div>
      <div className="flex justify-center w-full mt-8 gap-2 flex-wrap">
        <BlogCardPaginationButton
          href={buildPaginatedBlogUrl(currentPage - 1, selectedCategory, path)}
          disabled={!hasPrevPage}
          ariaLabel="Go to previous page"
        >
          <ChevronLeftIcon />
        </BlogCardPaginationButton>
        {shouldRenderLeftLimit && (
          <>
            <BlogCardPaginationButton
              href={buildPaginatedBlogUrl(1, selectedCategory, path)}
              active={currentPage === 1}
              ariaLabel="Go to page 1"
            >
              1
            </BlogCardPaginationButton>
            <span className="mx-4 py-3">...</span>
          </>
        )}
        {pagination.map((page) => (
          <BlogCardPaginationButton
            key={page}
            href={buildPaginatedBlogUrl(page, selectedCategory, path)}
            active={currentPage === page}
            ariaLabel={`Go to page ${page}`}
          >
            {page}
          </BlogCardPaginationButton>
        ))}
        {shouldRenderRightLimit && (
          <>
            <span className="mx-4 py-3 px-4">...</span>
            <BlogCardPaginationButton
              href={buildPaginatedBlogUrl(totalPages, selectedCategory, path)}
              active={currentPage === totalPages}
              ariaLabel={`Go to page ${totalPages}`}
            >
              {totalPages}
            </BlogCardPaginationButton>
          </>
        )}
        <BlogCardPaginationButton
          href={buildPaginatedBlogUrl(currentPage + 1, selectedCategory, path)}
          disabled={!hasNextPage}
          ariaLabel="Go to next page"
        >
          <ChevronRightIcon />
        </BlogCardPaginationButton>
      </div>
    </div>
  );
};
